import config from "../../config";
import { apiFetch, createMultiConsumerGet } from "./apiFetch";
import { requestError } from "./requestError";


// ─── Core HTTP helpers ────────────────────────────────────────────────────────
// Identical boilerplate to sales.service.js and purchase.service.js so the
// Finance module has zero runtime dependency on either of those files.

const getAccountId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.accountId;
};

const buildUrl = (path, params = {}) => {
    const filtered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const qs = new URLSearchParams({ account_id: getAccountId(), ...filtered });
    return `${config.apiUrl}/${path}?${qs}`;
};

const postJson = async (path, body) => {
    const res = await apiFetch(`${config.apiUrl}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...body }),
    });
    if (!res.ok) throw await requestError(res);
    return res.json();
};

const putJson = async (path, body) => {
    const res = await apiFetch(`${config.apiUrl}/${path}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...body }),
    });
    if (!res.ok) throw await requestError(res);
    return res.json();
};

const del = async (path, params) => {
    const res = await apiFetch(buildUrl(path, params), { method: "DELETE" });
    if (!res.ok) {
        // Keep the server's reason (why it can't be deleted), not just "HTTP 409".
        throw await requestError(res);
    }
    return res.json();
};

const sharedGet = createMultiConsumerGet(async (url, options) => {
    const res = await apiFetch(url, options);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
});
const get = (path, params = {}, options = {}) => {
    const url = buildUrl(path, params);
    return sharedGet(url, options);
};

/* ════════════════════════════════════════════════════════════════
   SALES CREDIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getCreditNotes = (p = {}, options = {}) => get("getCreditNotes", p, options);
export const getCNKpis = () => get("getCNKpis");
export const getCNById = (id, options = {}) => get("getCNById", { id }, options);
export const createCreditNote = (data) => postJson("createCreditNote", data);
export const updateCreditNote = (id, data) => putJson("updateCreditNote", { id, ...data });
export const deleteCreditNote = (id) => del("deleteCreditNote", { id });

/* ════════════════════════════════════════════════════════════════
   SALES DEBIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getDebitNotes = (p = {}, options = {}) => get("getDebitNotes", p, options);
export const getDNKpis = () => get("getDNKpis");
export const getDNById = (id, options = {}) => get("getDNById", { id }, options);
export const createDebitNote = (data) => postJson("createDebitNote", data);
export const updateDebitNote = (id, data) => putJson("updateDebitNote", { id, ...data });
export const deleteDebitNote = (id) => del("deleteDebitNote", { id });

/* ════════════════════════════════════════════════════════════════
   PURCHASE CREDIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getPurchaseCreditNotes = (p = {}, options = {}) => get("getPurchaseCreditNotes", p, options);
export const getPurchaseCreditNoteKpis = () => get("getPurchaseCreditNoteKpis");
export const getPurchaseCreditNoteById = (id, options = {}) => get("getPurchaseCreditNoteById", { id }, options);
export const createPurchaseCreditNote = (data) => postJson("createPurchaseCreditNote", data);
export const updatePurchaseCreditNote = (id, data) => putJson("updatePurchaseCreditNote", { id, ...data });
export const deletePurchaseCreditNote = (id) => del("deletePurchaseCreditNote", { id });

/* ════════════════════════════════════════════════════════════════
   PURCHASE DEBIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getPurchaseDebitNotes = (p = {}, options = {}) => get("getPurchaseDebitNotes", p, options);
export const getPurchaseDebitNoteKpis = () => get("getPurchaseDebitNoteKpis");
export const getPurchaseDebitNoteById = (id, options = {}) => get("getPurchaseDebitNoteById", { id }, options);
export const createPurchaseDebitNote = (data) => postJson("createPurchaseDebitNote", data);
export const updatePurchaseDebitNote = (id, data) => putJson("updatePurchaseDebitNote", { id, ...data });
export const deletePurchaseDebitNote = (id) => del("deletePurchaseDebitNote", { id });

/* ════════════════════════════════════════════════════════════════
   PAYMENT VOUCHER
   ════════════════════════════════════════════════════════════════ */
export const getPaymentVouchers = (p = {}, options = {}) => get("getPaymentVouchers", p, options);
export const getPaymentVoucherKpis = () => get("getPaymentVoucherKpis");
export const getPaymentVoucherById = (id, options = {}) => get("getPaymentVoucherById", { id }, options);
export const createPaymentVoucher = (data) => postJson("createPaymentVoucher", data);
export const updatePaymentVoucher = (id, data) => putJson("updatePaymentVoucher", { id, ...data });
export const deletePaymentVoucher = (id) => del("deletePaymentVoucher", { id });

/* ════════════════════════════════════════════════════════════════
   CURRENCY SETTINGS
   ════════════════════════════════════════════════════════════════ */
export const getCurrencySettings = (accountId) => get("getCurrencySettings", { company: accountId });